import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell, ImageRun, AlignmentType } from 'docx';
import { marked } from 'marked';

const HEADING_LINE_REGEX = /^(\d+(?:\.\d+)*)(?:\.)?\s+(.+)$/;
const HEADING_TRAILING_PUNCTUATION = /[.!?]$/;
const HEADING_FORBIDDEN_PATTERNS = [
  /\bmust\b/i,
  /\bshould\b/i,
  /\bwill\b/i,
  /\bshall\b/i,
];

const stripHeadingWrapper = (text = '') => text.replace(/^[\[{(<]+/, '').trim();

const deriveHeadingLevel = (numberPart = '') => {
  if (!numberPart) return 1;
  if (/\.0$/.test(numberPart)) return 1;
  return Math.min(numberPart.split('.').length, 6);
};

const parseNumberedHeading = (line = '') => {
  if (!line) return null;
  const trimmed = line.trim();
  if (!trimmed || /^#{1,6}\s/.test(trimmed)) return null;
  const match = trimmed.match(HEADING_LINE_REGEX);
  if (!match) return null;
  const [, numberPart, rawTitle] = match;
  const title = rawTitle.trim();
  if (!title) return null;
  const normalized = stripHeadingWrapper(title);
  if (!normalized) return null;
  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length > 18) return null;
  const firstChar = normalized.charAt(0);
  if (!/[A-Z]/.test(firstChar)) return null;
  if (HEADING_TRAILING_PUNCTUATION.test(normalized)) return null;
  const padded = ` ${normalized} `;
  if (HEADING_FORBIDDEN_PATTERNS.some((pattern) => pattern.test(padded))) {
    return null;
  }
  return {
    number: numberPart,
    title,
    level: deriveHeadingLevel(numberPart),
    normalizedTitle: normalized,
  };
};

const toDocxHeadingLevel = (numericLevel = 1) => {
  const level = Math.min(Math.max(numericLevel + 1, 1), 6);
  return HeadingLevel[`HEADING_${level}`] || HeadingLevel.HEADING_2;
};

const WordPreviewDialog = ({ 
  open, 
  onClose, 
  sections, 
  trialSchemaImageUrl, 
  generatedSoAData, 
  fontConfig, 
  titlePageConfig 
}) => {
  const [previewContent, setPreviewContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Import the same functions from DocumentEditor
  const markdownToDocxElements = (markdown, fontConfig) => {
    // Pre-process markdown to convert plain text headings to markdown headings
    let processedMarkdown = markdown || '';
    
    // Convert plain text headings to markdown headings
    // Pattern: lines that start with numbers like "1", "1.1", "1.1.1", etc.
    processedMarkdown = processedMarkdown.replace(/^(\d+\.?\s+[^\n]+)$/gm, (match) => {
      const headingInfo = parseNumberedHeading(match);
      if (!headingInfo || headingInfo.level !== 1) {
        return match;
      }
      return `# ${match.trim()}`;
    });
    
    // Then, handle subsection headings (multiple numbers like "1.1", "1.1.1")
    processedMarkdown = processedMarkdown.replace(/^(\d+\.\d+(?:\.\d+)?\s+[^\n]+)$/gm, (match) => {
      const headingInfo = parseNumberedHeading(match);
      if (!headingInfo || headingInfo.level < 2) {
        return match;
      }
      const prefix = '#'.repeat(Math.min(headingInfo.level, 6));
      return `${prefix} ${match.trim()}`;
    });
    
    const tokens = marked.lexer(processedMarkdown);
    const elements = [];
    
    // If no headings were detected, try direct processing
    if (tokens.filter(t => t.type === 'heading').length === 0) {
      const lines = processedMarkdown.split('\n');
      for (const line of lines) {
        const headingInfo = parseNumberedHeading(line);
        if (headingInfo) {
          const headingFontConfig = headingInfo.level === 1 ? fontConfig.heading : fontConfig.subheading;
          const paragraph = new Paragraph({
            children: [
              new TextRun({
                text: line.trim(),
                font: headingFontConfig.name,
                size: headingFontConfig.size * 2,
                bold: headingFontConfig.bold,
                color: headingFontConfig.color
              })
            ],
            heading: toDocxHeadingLevel(headingInfo.level),
            alignment: AlignmentType.LEFT,
            spacing: {
              line: Math.round(headingFontConfig.lineSpacing * 240),
              after: headingInfo.level === 1 ? 240 : 180,
              before: 120
            }
          });
          elements.push(paragraph);
          continue;
        }
        
        if (line.trim()) {
           // Regular paragraph
           const paragraph = new Paragraph({
             children: [
               new TextRun({
                 text: line,
                 font: fontConfig.body.name,
                 size: fontConfig.body.size * 2,
                 bold: fontConfig.body.bold,
                 color: fontConfig.body.color
               })
             ],
             alignment: AlignmentType.JUSTIFIED,
             spacing: {
               line: Math.round(fontConfig.body.lineSpacing * 240),
               before: 60 // Reduced from 120 to 60 for better spacing (60 twips = 3pt)
             }
           });
           console.log(`Created body paragraph with ${paragraph.children?.length || 0} children`);
           elements.push(paragraph);
         }
      }
      return elements;
    }
    
         for (const token of tokens) {
       if (token.type === 'heading') {
         const headingFontConfig = token.depth === 1 ? fontConfig.heading : fontConfig.subheading;
         const paragraph = new Paragraph({
           children: [
             new TextRun({
               text: token.text,
               font: headingFontConfig.name,
               size: headingFontConfig.size * 2,
               bold: headingFontConfig.bold,
               color: headingFontConfig.color
             })
           ],
           heading: token.depth === 1 ? HeadingLevel.HEADING_1 : HeadingLevel.HEADING_2,
           alignment: AlignmentType.LEFT,
           spacing: {
             line: Math.round(headingFontConfig.lineSpacing * 240),
             after: 240,
             before: 120 // Add space before headings for consistency
           }
         });
         console.log(`Created markdown heading paragraph with ${paragraph.children?.length || 0} children`);
         elements.push(paragraph);
       } else if (token.type === 'paragraph') {
         const paragraph = new Paragraph({
           children: [
             new TextRun({
               text: token.text,
               font: fontConfig.body.name,
               size: fontConfig.body.size * 2,
               bold: fontConfig.body.bold,
               color: fontConfig.body.color
             })
           ],
           alignment: AlignmentType.JUSTIFIED,
           spacing: {
             line: Math.round(fontConfig.body.lineSpacing * 240),
             before: 60 // Reduced from 120 to 60 for better spacing (60 twips = 3pt)
           }
         });
         console.log(`Created markdown paragraph with ${paragraph.children?.length || 0} children`);
         elements.push(paragraph);
       } else if (token.type === 'text') {
         const paragraph = new Paragraph({
           children: [
             new TextRun({
               text: token.text,
               font: fontConfig.body.name,
               size: fontConfig.body.size * 2,
               bold: fontConfig.body.bold,
               color: fontConfig.body.color
             })
           ],
           alignment: AlignmentType.JUSTIFIED,
           spacing: {
             line: Math.round(fontConfig.body.lineSpacing * 240),
             before: 60 // Reduced from 120 to 60 for better spacing (60 twips = 3pt)
           }
         });
         console.log(`Created markdown text paragraph with ${paragraph.children?.length || 0} children`);
         elements.push(paragraph);
       }
     }
    return elements;
  };

  const generateTitlePageContent = (config) => {
    if (!config.enabled) {
      return [];
    }
    
    const children = [];
    
    // Add proper spacing at the top
    children.push(new Paragraph({ 
      text: '',
      spacing: {
        after: 360 // Add 18pt space at the top (360 twips = 18pt)
      }
    }));
    
    // Add fields in the format shown in the template
    const fields = [
      'sponsorConfidentialityStatement',
      'fullTitle', 
      'trialAcronym',
      'sponsorProtocolIdentifier',
      'originalProtocol',
      'versionNumber',
      'versionDate',
      'amendmentIdentifier',
      'amendmentScope',
      'sponsorProductCodes',
      'investigationalProductNames',
      'trialPhase',
      'shortTitle'
    ];
    
    fields.forEach(fieldKey => {
      const field = config[fieldKey];
      
      if (field && field.enabled !== false) {
        const text = field.text || '';
        
        if (text.trim()) {
          children.push(
            new Paragraph({
              children: [
                // Label (bold)
                new TextRun({
                  text: field.label,
                  font: field.font || 'Times New Roman',
                  size: (field.size || 14) * 2,
                  bold: true, // Label is always bold
                  color: field.color || '000000'
                }),
                // Value (unbold)
                new TextRun({
                  text: ` ${text}`,
                  font: field.font || 'Times New Roman',
                  size: (field.size || 14) * 2,
                  bold: false, // Value is always unbold
                  color: field.color || '000000'
                })
              ],
              alignment: field.alignment === 'center' ? AlignmentType.CENTER : 
                        field.alignment === 'right' ? AlignmentType.RIGHT : AlignmentType.JUSTIFIED,
              spacing: {
                line: Math.round((field.lineSpacing || 1.0) * 240),
                before: 60,
                after: 120
              }
            })
          );
        }
      }
    });
    
    // Add proper spacing and page break
    children.push(new Paragraph({ 
      text: '',
      spacing: {
        before: 240
      },
      pageBreakBefore: true 
    }));
    
    return children;
  };

  const generatePreviewContent = async () => {
    setLoading(true);
    setError(null);
    
         try {
       console.log('Generating preview with:', {
         sectionsCount: sections.length,
         sectionsWithOutput: sections.filter(s => s.output && s.output.trim()).length,
         trialSchemaImageUrl: !!trialSchemaImageUrl,
         generatedSoAData: !!generatedSoAData
       });
       
       // Debug: Check first few sections for content
       console.log('First 3 sections content check:', sections.slice(0, 3).map((section, index) => ({
         index,
         title: section.title,
         hasOutput: !!section.output,
         outputLength: section.output?.length || 0,
         outputPreview: section.output?.substring(0, 100) + '...' || 'N/A'
       })));
       
       const children = [];
       
       // Fetch title page config if not provided
      let currentTitlePageConfig = titlePageConfig;
      if (!currentTitlePageConfig) {
        try {
          // Import the function from DocumentEditor (simplified version)
          const getTitlePageConfigWithData = async () => {
            try {
              // For preview, we'll use a simplified version
              return {
                enabled: true,
                sponsorConfidentialityStatement: {
                  label: 'Sponsor Confidentiality Statement:',
                  text: 'This document contains confidential information.',
                  font: 'Times New Roman',
                  size: 14,
                  bold: true,
                  color: '000000',
                  alignment: 'left',
                  lineSpacing: 1.5
                },
                fullTitle: {
                  label: 'Full Title:',
                  text: 'Clinical Trial Protocol',
                  font: 'Times New Roman',
                  size: 14,
                  bold: true,
                  color: '000000',
                  alignment: 'left',
                  lineSpacing: 1.5
                },
                // Add other fields as needed
              };
            } catch (error) {
              console.error('Error fetching title page config:', error);
              return null;
            }
          };
          
          currentTitlePageConfig = await getTitlePageConfigWithData();
        } catch (error) {
          console.error('Error fetching title page config for preview:', error);
        }
      }
      
             // Add title page if configured
       console.log('Title page config:', currentTitlePageConfig);
       // Temporarily disable title page to test sections
       if (false && currentTitlePageConfig && currentTitlePageConfig.enabled) {
         const titlePageContent = generateTitlePageContent(currentTitlePageConfig);
         console.log('Title page content length:', titlePageContent.length);
         children.push(...titlePageContent);
       }
      
      // Add document title (if no title page or as fallback)
      if (!currentTitlePageConfig || !currentTitlePageConfig.enabled) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Clinical Trial Protocol",
                font: fontConfig.title.name,
                size: fontConfig.title.size * 2,
                bold: fontConfig.title.bold,
                color: fontConfig.title.color
              })
            ],
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: {
              line: Math.round(fontConfig.title.lineSpacing * 240),
              after: 360
            }
          }),
          new Paragraph({ text: '' })
        );
      }
      
      let _encounteredAnySection = false;
      for (const section of sections) {
        if (section.output && section.output.trim()) {
           if (_encounteredAnySection) {
            children.push(new Paragraph({ children: [], pageBreakBefore: true }));
          }
          // Only add titles for Trial Schema and Schedule of Activities sections
          if (section.title === '1.2 TRIAL SCHEMA' || section.title === '1.3 SCHEDULE OF ACTIVITIES') {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: section.title,
                    font: fontConfig.heading.name,
                    size: fontConfig.heading.size * 2,
                    bold: fontConfig.heading.bold,
                    color: fontConfig.heading.color
                  })
                ],
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.LEFT,
                spacing: {
                  line: Math.round(fontConfig.heading.lineSpacing * 240),
                  after: 240,
                  before: 120
                }
              })
            );
          }
          
          // Special handling for Trial Schema
          if (section.title === '1.2 TRIAL SCHEMA' && trialSchemaImageUrl) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: '[Trial Schema Image]',
                    font: fontConfig.body.name,
                    size: fontConfig.body.size * 2,
                    bold: false,
                    color: fontConfig.body.color
                  })
                ],
                alignment: AlignmentType.CENTER,
                spacing: {
                  line: Math.round(fontConfig.body.lineSpacing * 240),
                  before: 60
                }
              })
            );
          }
          // Special handling for SOA
          else if (section.title === '1.3 SCHEDULE OF ACTIVITIES' && generatedSoAData) {
            // Export the narrative first
            const docxElements = markdownToDocxElements(section.output, fontConfig);
            if (docxElements.length > 0) {
              children.push(...docxElements);
            }
            
            // Then add SOA table placeholder
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: '[Schedule of Activities Table]',
                    font: fontConfig.body.name,
                    size: fontConfig.body.size * 2,
                    bold: false,
                    color: fontConfig.body.color
                  })
                ],
                alignment: AlignmentType.CENTER,
                spacing: {
                  line: Math.round(fontConfig.body.lineSpacing * 240),
                  before: 60
                }
              })
            );
          }
          // All other sections
          else {
            console.log(`Processing section "${section.title}" with output length:`, section.output?.length || 0);
            const docxElements = markdownToDocxElements(section.output, fontConfig);
            console.log(`  Generated ${docxElements.length} docx elements for section "${section.title}"`);
            if (docxElements.length > 0) {
              children.push(...docxElements);
            }
          }
          
          children.push(new Paragraph({ text: '' }));
          _encounteredAnySection = true;
        }
      }
      
             console.log('Final children array length:', children.length);
       console.log('Children array:', children.map(el => ({
         type: el.constructor.name,
         text: el.text || (el.children ? el.children.map(c => c.text).join('') : '')
       })));
       
       
       
               // Convert to readable text for preview
        let previewText = '';
        let extractedElements = 0;
        
        console.log('Starting text extraction from', children.length, 'elements');
        
        // Instead of trying to extract from docx elements, let's build the text as we create the elements
        // We'll store the text content separately as we process each section
        const textContent = [];
        
        // Add document title
        if (!currentTitlePageConfig || !currentTitlePageConfig.enabled) {
          textContent.push("Clinical Trial Protocol\n");
        }
        
        // Process sections and build text content
        for (const section of sections) {
          if (section.output && section.output.trim()) {
            // Only add titles for Trial Schema and Schedule of Activities sections
            if (section.title === '1.2 TRIAL SCHEMA' || section.title === '1.3 SCHEDULE OF ACTIVITIES') {
              textContent.push(`${section.title}\n`);
            }
            
            // Special handling for Trial Schema
            if (section.title === '1.2 TRIAL SCHEMA' && trialSchemaImageUrl) {
              textContent.push('[Trial Schema Image]\n');
            }
            // Special handling for SOA
            else if (section.title === '1.3 SCHEDULE OF ACTIVITIES' && generatedSoAData) {
              // Add the narrative content
              textContent.push(section.output + '\n');
              textContent.push('[Schedule of Activities Table]\n');
            }
            // All other sections
            else {
              console.log(`Processing section "${section.title}" with output length:`, section.output?.length || 0);
              textContent.push(section.output + '\n');
            }
            
            textContent.push('\n'); // Add spacing between sections
          }
        }
        
        // Join all text content
        previewText = textContent.join('');
        extractedElements = textContent.length;
        
        console.log('Text content built:', {
          sectionsProcessed: sections.filter(s => s.output && s.output.trim()).length,
          textContentLength: textContent.length,
          previewTextLength: previewText.length,
          sampleText: previewText.substring(0, 200) + '...'
        });
       
       console.log('Text extraction results:', {
         totalElements: children.length,
         extractedElements,
         previewTextLength: previewText.length,
         sampleText: previewText.substring(0, 200) + '...'
       });
       
       // If no content was generated, show a fallback message
       if (!previewText.trim()) {
         previewText = 'No content available for preview.\n\nPlease ensure you have sections with content in your document.';
       }
       
              // Clean up the text to remove any HTML-like content and ensure proper formatting
       const cleanPreviewText = previewText
         .replace(/<[^>]*>/g, '') // Remove any HTML tags
         .replace(/\n\s*\n/g, '\n\n') // Normalize multiple newlines
         .trim();
       
       console.log('Preview content generated:', {
         originalLength: previewText.length,
         cleanedLength: cleanPreviewText.length,
         previewText: previewText.substring(0, 200) + '...',
         cleanPreviewText: cleanPreviewText.substring(0, 200) + '...'
       });
       
       setPreviewContent(cleanPreviewText);
    } catch (error) {
      console.error('Error generating preview:', error);
      setError('Failed to generate preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      generatePreviewContent();
    }
  }, [open, sections, trialSchemaImageUrl, generatedSoAData, fontConfig, titlePageConfig]);

  const handleDownload = async () => {
    try {
      setLoading(true);
      
      // Create the actual Word document
      const children = [];
      
      // Add title page if configured
      if (titlePageConfig && titlePageConfig.enabled) {
        children.push(...generateTitlePageContent(titlePageConfig));
      }
      
      // Add document title (if no title page or as fallback)
      if (!titlePageConfig || !titlePageConfig.enabled) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Clinical Trial Protocol",
                font: fontConfig.title.name,
                size: fontConfig.title.size * 2,
                bold: fontConfig.title.bold,
                color: fontConfig.title.color
              })
            ],
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: {
              line: Math.round(fontConfig.title.lineSpacing * 240),
              after: 360
            }
          }),
          new Paragraph({ text: '' })
        );
      }
      
      let _encounteredAnySectionForDownload = false;
      for (const section of sections) {
        if (section.output && section.output.trim()) {
          if (_encounteredAnySectionForDownload) {
            children.push(new Paragraph({ children: [], pageBreakBefore: true }));
          }
          // Only add titles for Trial Schema and Schedule of Activities sections
          if (section.title === '1.2 TRIAL SCHEMA' || section.title === '1.3 SCHEDULE OF ACTIVITIES') {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: section.title,
                    font: fontConfig.heading.name,
                    size: fontConfig.heading.size * 2,
                    bold: fontConfig.heading.bold,
                    color: fontConfig.heading.color
                  })
                ],
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.LEFT,
                spacing: {
                  line: Math.round(fontConfig.heading.lineSpacing * 240),
                  after: 240,
                  before: 120
                }
              })
            );
          }
          
          // Special handling for Trial Schema
          if (section.title === '1.2 TRIAL SCHEMA' && trialSchemaImageUrl) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: '[Trial Schema Image]',
                    font: fontConfig.body.name,
                    size: fontConfig.body.size * 2,
                    bold: false,
                    color: fontConfig.body.color
                  })
                ],
                alignment: AlignmentType.CENTER,
                spacing: {
                  line: Math.round(fontConfig.body.lineSpacing * 240),
                  before: 60
                }
              })
            );
          }
          // Special handling for SOA
          else if (section.title === '1.3 SCHEDULE OF ACTIVITIES' && generatedSoAData) {
            // Export the narrative first
            const docxElements = markdownToDocxElements(section.output, fontConfig);
            if (docxElements.length > 0) {
              children.push(...docxElements);
            }
            
            // Then add SOA table placeholder
            children.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: '[Schedule of Activities Table]',
                    font: fontConfig.body.name,
                    size: fontConfig.body.size * 2,
                    bold: false,
                    color: fontConfig.body.color
                  })
                ],
                alignment: AlignmentType.CENTER,
                spacing: {
                  line: Math.round(fontConfig.body.lineSpacing * 240),
                  before: 60
                }
              })
            );
          }
          // All other sections
          else {
            const docxElements = markdownToDocxElements(section.output, fontConfig);
            if (docxElements.length > 0) {
              children.push(...docxElements);
            }
          }
          
          children.push(new Paragraph({ text: '' }));
          _encounteredAnySectionForDownload = true;
        }
      }
      
      const doc = new Document({
        sections: [{ children }],
      });
      
      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'clinical-trial-protocol-preview.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading preview:', error);
      setError('Failed to download preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f5f5f5'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Word Document Preview
        </Typography>
        <Box>
          <Tooltip title="Refresh Preview">
            <IconButton 
              onClick={generatePreviewContent}
              disabled={loading}
              size="small"
              sx={{ mr: 1 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Generating preview...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box
            sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              p: 3,
              minHeight: '400px',
              maxHeight: '60vh',
              overflow: 'auto',
              wordBreak: 'break-word'
            }}
          >
            {previewContent ? (
              <div
                style={{
                  fontFamily: fontConfig?.body?.name || 'Times New Roman, serif',
                  fontSize: `${fontConfig?.body?.size || 14}px`,
                  lineHeight: fontConfig?.body?.lineSpacing || 1.5,
                  textAlign: 'justify',
                  whiteSpace: 'pre-wrap'
                }}
                dangerouslySetInnerHTML={{
                  __html: previewContent
                    .split('\n')
                    .map((line, index) => {
                      // Apply document title formatting
                      if (line === 'Clinical Trial Protocol') {
                        return `<h1 style="
                          font-family: ${fontConfig?.title?.name || 'Times New Roman, serif'};
                          font-size: ${(fontConfig?.title?.size || 18) * 1.3}px;
                          font-weight: ${fontConfig?.title?.bold ? 'bold' : 'normal'};
                          color: #${fontConfig?.title?.color || '000000'};
                          margin: 18px 0 12px 0;
                          line-height: ${fontConfig?.title?.lineSpacing || 1.5};
                          text-align: center;
                        ">${line}</h1>`;
                      }
                      
                      // Apply heading formatting
                      if (line.match(/^(\d+\.?\s+[^\n]+)$/)) {
                        return `<h1 style="
                          font-family: ${fontConfig?.heading?.name || 'Times New Roman, serif'};
                          font-size: ${(fontConfig?.heading?.size || 16) * 1.2}px;
                          font-weight: ${fontConfig?.heading?.bold ? 'bold' : 'normal'};
                          color: #${fontConfig?.heading?.color || '000000'};
                          margin: 12px 0 6px 0;
                          line-height: ${fontConfig?.heading?.lineSpacing || 1.5};
                          text-align: left;
                        ">${line}</h1>`;
                      }
                      
                      // Apply special section titles (Trial Schema and Schedule of Activities)
                      if (line === '1.2 TRIAL SCHEMA' || line === '1.3 SCHEDULE OF ACTIVITIES') {
                        return `<h2 style="
                          font-family: ${fontConfig?.heading?.name || 'Times New Roman, serif'};
                          font-size: ${(fontConfig?.heading?.size || 16) * 1.1}px;
                          font-weight: ${fontConfig?.heading?.bold ? 'bold' : 'normal'};
                          color: #${fontConfig?.heading?.color || '000000'};
                          margin: 10px 0 5px 0;
                          line-height: ${fontConfig?.heading?.lineSpacing || 1.5};
                          text-align: left;
                        ">${line}</h2>`;
                      }
                      
                      if (line.match(/^(\d+\.\d+(?:\.\d+)?\s+[^\n]+)$/)) {
                        return `<h2 style="
                          font-family: ${fontConfig?.subheading?.name || 'Times New Roman, serif'};
                          font-size: ${(fontConfig?.subheading?.size || 14) * 1.1}px;
                          font-weight: ${fontConfig?.subheading?.bold ? 'bold' : 'normal'};
                          color: #${fontConfig?.subheading?.color || '000000'};
                          margin: 10px 0 5px 0;
                          line-height: ${fontConfig?.subheading?.lineSpacing || 1.5};
                          text-align: left;
                        ">${line}</h2>`;
                      }
                      
                      // Apply body text formatting
                      if (line.trim()) {
                        return `<p style="
                          font-family: ${fontConfig?.body?.name || 'Times New Roman, serif'};
                          font-size: ${fontConfig?.body?.size || 14}px;
                          font-weight: ${fontConfig?.body?.bold ? 'bold' : 'normal'};
                          color: #${fontConfig?.body?.color || '000000'};
                          margin: 3px 0;
                          line-height: ${fontConfig?.body?.lineSpacing || 1.5};
                          text-align: justify;
                        ">${line}</p>`;
                      }
                      
                      // Empty lines
                      return '<br>';
                    })
                    .join('')
                }}
              />
            ) : (
              <Typography variant="body1" color="text.secondary" textAlign="center">
                No content to preview
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#f5f5f5',
        p: 2
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          Close
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={loading || !previewContent}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0'
            }
          }}
        >
          Download Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WordPreviewDialog;
